import { createRouter, createWebHashHistory } from "vue-router";
import Home from "../views/Home.vue";
import store from "../store";
import cloudloop from "@/mixins/cloudloop";
import { applyNavigationMeta } from "@/kit/pageTitle.js";
import { prettySessionName } from "@/kit/sessionName.js";
import { isQueryOnly, redirectFor } from "./redirects.js";

// Route meta:
//   title        the tab title before " · cloudloop" (a string, or a
//                function of the route); none on Home. Pages that load a
//                better one (a session, a pack) call setPageTitle().
//   description  the meta description; the site's line when absent.
//   bareForGuest signed out, the page is the whole window (Welcome, the
//                auth forms, an invitation): App.vue hides the top bar.
//   guestFrame   always the guest frame, signed in or not (the auth pages:
//                logInUser commits the user while /login is still current).
//   requiresAuth false for public pages; anything else needs an account.
// The shell's meta (SIGNED_IN_PLAN sections 3 and 4):
//   place        the sidebar's selection (shell/places.js ids); "@session"
//                is the last session list; none keeps the last place.
//   shell        "pane", "session", "overlay", "full" or "bare".
//   overlay      "inbox" or "settings": which overlay the route is.
//   ownHeader    the page draws its own phone nav; otherwise the shell
//                draws a generic PhoneNav ("‹ Sessions", or phoneBack).
//   phoneBack    the generic PhoneNav's back label.
const param = (name) => (route) => {
  const v = route.params[name];
  return typeof v === "string" ? v : "";
};

const me = () => store.state.loggedInUser?.username || null;
const legacy = (to) => redirectFor(to, { me: me() }) || "/sessions/mine";

// A signed-in place (a lane's page): its own phone header, inside the shell.
const place = (id, title, extra = {}) => ({
  requiresAuth: true,
  shell: "pane",
  ownHeader: true,
  place: id,
  title,
  ...extra,
});

const SessionsPane = () => import("../views/SessionsPane.vue");
const MyAudio = () => import("../views/MyAudio.vue");

const routes = [
  {
    path: "/",
    name: "Home",
    component: Home,
    meta: { requiresAuth: false, bareForGuest: true, shell: "bare" },
  },
  {
    path: "/about",
    name: "About",
    component: () => import("../views/About.vue"),
    meta: {
      requiresAuth: false,
      title: "About",
      description:
        "A global, collaborative loop station. Upload loops, jam in real time with anyone, and pull stems back down.",
    },
  },

  // Sessions (W2)
  { path: "/sessions/mine", name: "SessionsMine", component: SessionsPane, meta: place("mine", "Mine") },
  { path: "/sessions/friends", name: "SessionsFriends", component: SessionsPane, meta: place("friends", "Friends") },
  { path: "/sessions/live", name: "SessionsLive", component: SessionsPane, meta: place("live", "Live") },
  { path: "/discover", name: "Discover", component: () => import("../views/Discover.vue"), meta: place("discover", "Discover") },
  { path: "/discover/liked", name: "Liked", component: () => import("../views/Liked.vue"), meta: place("liked", "Liked") },
  { path: "/search", name: "Search", component: () => import("../views/Search.vue"), meta: place("@session", "Search") },

  {
    // Public: logged-out visitors can view/play a public session (read-only).
    // Private sessions still gate inside Session.vue via the public endpoint,
    // which 404s them for anonymous callers.
    path: "/session/:sessionName?",
    name: "Session",
    component: () => import("../views/Session.vue"),
    meta: {
      requiresAuth: false,
      shell: "session",
      ownHeader: true,
      place: "@session",
      title: (route) => prettySessionName(param("sessionName")(route)) || "Session",
    },
  },

  // Sounds (W3)
  { path: "/sounds", name: "Sounds", component: () => import("../views/Sounds.vue"), meta: place("browse", "Sounds") },
  { path: "/packs", name: "Packs", component: () => import("../views/Packs.vue"), meta: place("packs", "Packs") },
  {
    path: "/pack/:slug",
    name: "PackPage",
    component: () => import("../views/PackPage.vue"),
    meta: { ...place("packs", "Pack"), requiresAuth: false },
  },

  // Library (W3b)
  { path: "/library/my-audio", name: "MyAudio", component: MyAudio, meta: place("my-audio", "My audio") },
  { path: "/library/favorites", name: "Favorites", component: MyAudio, meta: place("favorites", "Favorites") },

  // Social (W4)
  {
    path: "/people/:userName?",
    name: "People",
    component: () => import("../views/People.vue"),
    meta: place("people", "People"),
  },
  {
    // The full-pane profile (SI-23): it keeps the last place selected.
    path: "/users/:userName",
    name: "Profile",
    component: () => import("../views/Profile"),
    meta: { requiresAuth: true, shell: "pane", ownHeader: true, title: param("userName") },
  },
  {
    // The app's Inbox is a sheet at every width, over the last pane.
    path: "/inbox",
    name: "Inbox",
    component: () => import("../components/social/InboxSheet.vue"),
    meta: { requiresAuth: true, shell: "overlay", overlay: "inbox", ownHeader: true, title: "Inbox" },
  },

  // Settings (W4b): the window at pointer density from 700 px, else the
  // pushed page. No tab is General (SI-9).
  {
    path: "/settings/:tab(general|account|storage|payments)?",
    name: "Settings",
    component: () => import("../views/Settings.vue"),
    meta: { requiresAuth: true, shell: "overlay", overlay: "settings", ownHeader: true, title: "Settings" },
  },
  {
    // Public on purpose: this is the account-deletion URL given to Google
    // Play. Signed-out visitors see what deletion does and a sign-in
    // button; signed in it opens Settings › Account's delete modal.
    path: "/delete-account",
    name: "DeleteAccount",
    component: () => import("../views/AccountSettings.vue"),
    meta: { requiresAuth: false, title: "Delete account" },
  },

  {
    // The kit gallery W1 grades the shared rows and bars on (SIGNED_IN_PLAN
    // section 5). Only with localStorage cl_kit_gallery = "1".
    path: "/__kit/:board",
    name: "KitGallery",
    component: () => import("../views/KitGallery.vue"),
    meta: { requiresAuth: true, shell: "pane", ownHeader: true, title: "Kit" },
    beforeEnter: () => {
      let on = false;
      try {
        on = window.localStorage.getItem("cl_kit_gallery") === "1";
      } catch (e) {
        on = false;
      }
      return on ? true : { path: "/", replace: true };
    },
  },

  // Old paths keep working (router/redirects.js).
  { path: "/sessions", redirect: legacy },
  { path: "/my-audio", redirect: legacy },
  { path: "/license-studio", redirect: legacy },
  { path: "/users/:userName/storage", redirect: legacy },

  {
    path: "/get-started",
    name: "GetStarted",
    component: () => import("../views/GetStarted.vue"),
    meta: { requiresAuth: true, title: "Get started" },
  },
  {
    path: "/login",
    name: "Login",
    component: () => import("../views/Login.vue"),
    meta: { requiresAuth: false, title: "Log in", bareForGuest: true, guestFrame: true },
  },
  {
    path: "/invite/:token",
    name: "AcceptInvitation",
    component: () => import("../views/AcceptInvitation.vue"),
    meta: { requiresAuth: false, title: "Invitation", bareForGuest: true },
  },
  {
    path: "/sign-up",
    name: "SignUp",
    component: () => import("../views/SignUp.vue"),
    meta: { requiresAuth: false, title: "Create account", bareForGuest: true, guestFrame: true },
  },
  {
    path: "/forgot-password",
    name: "ForgotPassword",
    component: () => import("../views/ForgotPassword.vue"),
    meta: { requiresAuth: false, title: "Reset password", bareForGuest: true, guestFrame: true },
  },
  {
    path: "/reset-password",
    name: "ResetPassword",
    component: () => import("../views/ResetPassword.vue"),
    meta: {
      requiresAuth: false,
      title: "Set a new password",
      bareForGuest: true,
      guestFrame: true,
    },
  },
  {
    path: "/purchase/success",
    name: "PurchaseSuccess",
    component: () => import("../views/PurchaseSuccess.vue"),
    meta: { requiresAuth: true, title: "Purchase complete" },
  },
  {
    path: "/purchase/cancel",
    name: "PurchaseCancel",
    component: () => import("../views/PurchaseCancel.vue"),
    meta: { requiresAuth: true, title: "Checkout cancelled" },
  },
  {
    path: "/pack-purchase/success",
    name: "PackPurchaseSuccess",
    component: () => import("../views/PackPurchaseSuccess.vue"),
    meta: { requiresAuth: true, title: "Purchase complete" },
  },
  {
    path: "/pack-purchase/cancel",
    name: "PackPurchaseCancel",
    component: () => import("../views/PurchaseCancel.vue"),
    meta: { requiresAuth: true, title: "Checkout cancelled" },
  },
  {
    path: "/terms",
    name: "Terms",
    component: () => import("../views/Terms.vue"),
    meta: {
      requiresAuth: false,
      title: "Terms of service",
      description: "The terms for using cloudloop.",
    },
  },
  {
    path: "/refunds",
    name: "RefundPolicy",
    component: () => import("../views/RefundPolicy.vue"),
    meta: {
      requiresAuth: false,
      title: "Refund policy",
      description: "When cloudloop purchases can be refunded, and how to ask.",
    },
  },
  {
    path: "/dmca",
    name: "DmcaPolicy",
    component: () => import("../views/DmcaPolicy.vue"),
    meta: {
      requiresAuth: false,
      title: "Copyright & DMCA policy",
      description:
        "How cloudloop handles copyright notices and counter-notices.",
    },
  },
  {
    path: "/privacy",
    name: "PrivacyPolicy",
    component: () => import("../views/PrivacyPolicy.vue"),
    meta: {
      requiresAuth: false,
      title: "Privacy policy",
      description: "What cloudloop collects, why, and what we do with it.",
    },
  },
];

const router = createRouter({
  history: createWebHashHistory(),
  routes,
});

// Referral attribution: share links carry ?via=<username> (the share pages
// re-inject it inside the hash). Remember the most recent referrer so a
// later sign-up can credit them — cleared when SignUp consumes it.
router.afterEach((to, from, failure) => {
  applyNavigationMeta(to, failure);
  const via = to.query.via;
  if (typeof via === "string" && via && via.length <= 64) {
    window.localStorage.setItem("pendingReferrer", via);
  }
  // A session or profile shows its share path (/s/<name>, /u/<name>) in the
  // address bar (filters/utils showShareablePath). Leaving it, go back to /
  // so a reload of /s/X#/sessions/mine doesn't land on the old session
  // through the server's share route.
  const shared = ["Session", "Profile"];
  if (!failure && shared.includes(from.name) && !shared.includes(to.name)) {
    try {
      if (window.location.pathname !== "/") {
        window.history.replaceState(window.history.state, "", `/${window.location.hash}`);
      }
    } catch (e) {
      /* not ours to rewrite */
    }
  }
});

// A successful validateToken holds for the page's life, per token; a new
// token validates again. A 401 from any API call drops the user anyway
// (services/api/client.js).
let validated = { token: null, ok: false };

// Navigation guard to check authentication
router.beforeEach(async (to, from, next) => {
  const requiresAuth = to.meta.requiresAuth !== false; // Default to requiring auth
  const hasUser = store.state.loggedInUser !== null;
  const token = window.localStorage.getItem("JWT");

  // A persisted loggedInUser is not proof the token still works. Confirm
  // it against the API before treating the route as authorized. Same-path
  // navigations that only change the query (typing in Search) skip it.
  let tokenValid = false;
  if (hasUser && !!token) {
    if (validated.token === token && (validated.ok || isQueryOnly(to, from))) {
      tokenValid = validated.ok;
    } else {
      tokenValid = await cloudloop.methods.validateToken();
      validated = { token, ok: tokenValid };
    }
  }

  // Stale session: drop it so the UI stops showing a logged-in state we
  // can no longer authenticate.
  if (hasUser && !tokenValid) {
    window.localStorage.removeItem("JWT");
    store.commit("setLoggedInUser", null);
  }

  // Signed in, / is Sessions: Mine, and the Google Play deletion URL opens
  // Settings › Account (router/redirects.js).
  const moved = redirectFor(to, { me: hasUser && tokenValid ? me() : null });
  if (moved) {
    next(moved);
    return;
  }

  if (requiresAuth && !tokenValid) {
    // Kick unauthenticated/expired sessions to login, but preserve where they
    // were headed so they land back there after signing in (deep-link intact).
    next({ name: "Login", query: { redirect: to.fullPath } });
  } else {
    next();
  }
});

export default router;
